import type { Lesson, LessonNote, LessonProgress } from '../../types/models';
import { mockCourses } from './mock-courses';

const isBrowser = typeof window !== 'undefined';
const NOTES_KEY = 'mock_lesson_notes';
const PROGRESS_KEY = 'mock_lesson_progress';

type LessonNoteInput = Omit<LessonNote, 'id' | 'userId' | 'createdAt'>;

const nowIso = (): string => new Date().toISOString();

const buildCourseLessons = (courseId: string): ReadonlyArray<Lesson> => {
  const course = mockCourses.find((c) => c.id === courseId);
  if (!course) return [];

  const baseVideo = course.trailerUrl || 'https://www.w3schools.com/html/mov_bbb.mp4';

  return [
    {
      id: `${courseId}-l1`,
      courseId,
      title: 'Welcome and Course Setup',
      description: 'Get your environment configured and understand the bootcamp roadmap.',
      videoUrl: baseVideo,
      duration: 640,
      order: 1,
      isFree: true,
      resources: [
        {
          id: `${courseId}-r1`,
          title: 'Setup Checklist',
          type: 'pdf',
          url: 'https://example.com/setup-checklist.pdf',
        },
      ],
      createdAt: nowIso(),
    },
    {
      id: `${courseId}-l2`,
      courseId,
      title: 'Core Concepts Deep Dive',
      description: 'Break down foundational concepts with practical examples and exercises.',
      videoUrl: baseVideo,
      duration: 980,
      order: 2,
      isFree: false,
      resources: [
        {
          id: `${courseId}-r2`,
          title: 'Reference Notes',
          type: 'pdf',
          url: 'https://example.com/reference-notes.pdf',
        },
      ],
      createdAt: nowIso(),
    },
    {
      id: `${courseId}-l3`,
      courseId,
      title: 'Build Project Part 1',
      description: 'Start implementing your feature set with mentor-guided architecture decisions.',
      videoUrl: baseVideo,
      duration: 1240,
      order: 3,
      isFree: false,
      resources: [
        {
          id: `${courseId}-r3`,
          title: 'Starter Repo',
          type: 'link',
          url: 'https://github.com',
        },
      ],
      createdAt: nowIso(),
    },
    {
      id: `${courseId}-l4`,
      courseId,
      title: 'Build Project Part 2 and Review',
      description: 'Finish your project, test it, and review production hardening strategies.',
      videoUrl: baseVideo,
      duration: 1360,
      order: 4,
      isFree: false,
      resources: [
        {
          id: `${courseId}-r4`,
          title: 'Deployment Guide',
          type: 'pdf',
          url: 'https://example.com/deployment-guide.pdf',
        },
      ],
      createdAt: nowIso(),
    },
  ];
};

const allMockLessons: ReadonlyArray<Lesson> = mockCourses.flatMap((course) =>
  buildCourseLessons(course.id)
);

const readRecord = <T>(key: string): Record<string, T> => {
  if (!isBrowser) return {};
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Record<string, T>) : {};
  } catch {
    return {};
  }
};

const writeRecord = <T>(key: string, value: Record<string, T>): void => {
  if (!isBrowser) return;
  localStorage.setItem(key, JSON.stringify(value));
};

export const getMockLessonsByCourse = (courseId: string): ReadonlyArray<Lesson> =>
  allMockLessons.filter((lesson) => lesson.courseId === courseId).sort((a, b) => a.order - b.order);

export const getMockLessonById = (lessonId: string): Lesson | undefined =>
  allMockLessons.find((lesson) => lesson.id === lessonId);

export const getMockProgress = (lessonId: string): LessonProgress => {
  const record = readRecord<LessonProgress>(PROGRESS_KEY);
  return (
    record[lessonId] || {
      lessonId,
      userId: 'mock_user',
      completed: false,
      watchedSeconds: 0,
      lastWatchedAt: nowIso(),
    }
  );
};

export const updateMockProgress = (
  lessonId: string,
  updates: Partial<LessonProgress>
): LessonProgress => {
  const current = getMockProgress(lessonId);
  const next: LessonProgress = {
    ...current,
    ...updates,
    lessonId,
    userId: updates.userId || current.userId || 'mock_user',
    lastWatchedAt: updates.lastWatchedAt || nowIso(),
  };

  const record = readRecord<LessonProgress>(PROGRESS_KEY);
  writeRecord(PROGRESS_KEY, { ...record, [lessonId]: next });
  return next;
};

export const getMockNotes = (lessonId: string): ReadonlyArray<LessonNote> => {
  const record = readRecord<ReadonlyArray<LessonNote>>(NOTES_KEY);
  return record[lessonId] || [];
};

export const createMockNote = (
  lessonId: string,
  note: LessonNoteInput
): LessonNote => {
  const next: LessonNote = {
    id: `note_${lessonId}_${Date.now()}`,
    lessonId,
    userId: 'mock_user',
    content: note.content,
    timestamp: note.timestamp,
    createdAt: nowIso(),
  };

  const record = readRecord<ReadonlyArray<LessonNote>>(NOTES_KEY);
  const existing = record[lessonId] || [];
  writeRecord(NOTES_KEY, { ...record, [lessonId]: [...existing, next] });
  return next;
};

export const updateMockNote = (
  noteId: string,
  updates: Partial<LessonNote>
): LessonNote | undefined => {
  const record = readRecord<ReadonlyArray<LessonNote>>(NOTES_KEY);
  const entries = Object.entries(record);

  for (const [lessonId, notes] of entries) {
    const index = notes.findIndex((note) => note.id === noteId);
    if (index >= 0) {
      const updated: LessonNote = { ...notes[index], ...updates, id: noteId };
      const nextNotes = [...notes];
      nextNotes[index] = updated;
      writeRecord(NOTES_KEY, { ...record, [lessonId]: nextNotes });
      return updated;
    }
  }

  return undefined;
};

export const deleteMockNote = (noteId: string): boolean => {
  const record = readRecord<ReadonlyArray<LessonNote>>(NOTES_KEY);
  const entries = Object.entries(record);

  for (const [lessonId, notes] of entries) {
    const nextNotes = notes.filter((note) => note.id !== noteId);
    if (nextNotes.length !== notes.length) {
      writeRecord(NOTES_KEY, { ...record, [lessonId]: nextNotes });
      return true;
    }
  }

  return false;
};
